/**
 * Global type definitions for the ArchiWeb application
 */

// Base entity interface
export interface BaseEntity {
  id: string | number
  createdAt?: string | Date
  updatedAt?: string | Date
}

// User related types
export interface User extends BaseEntity {
  email: string
  firstName: string
  lastName: string
  phone?: string
  roles?: string[]
  permissions?: string[]
  userType?: 'FIRM_OWNER' | 'FIRM_MEMBER' | 'CLIENT' | 'ADMIN'
  type: 'FIRM_OWNER' | 'FIRM_MEMBER' | 'CLIENT' | 'ADMIN'
  isActive: boolean
  lastLoginAt?: string | Date
  lastUpdated?: string | Date
  profilePicture?: string
  creationDate?: string | Date
  
  // Settings fields (now directly part of user)
  currency?: string
  language?: string
  timezone?: string
  dateFormat?: string
  
  // Legacy field for backward compatibility
  settings?: UserSettings
  projectRoles?: Record<string, string>
  projectPermissions?: Record<string, string[]>
}

export interface UserSettings {
  currency: string
  language: string
  timezone: string
  dateFormat?: string
}

// NotificationSettings is deprecated - notifications are now handled separately
export interface NotificationSettings {
  email: boolean
  push: boolean
  sms: boolean
}

// Authentication types
export interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  refreshToken: string | null
  expiresAt: number | null
}

export interface LoginCredentials {
  username: string
  email?: string
  password: string
  rememberMe?: boolean
}

export interface AuthResponse {
  success: boolean
  token?: string
  accessToken?: string
  refreshToken?: string
  user?: User
  expiresIn?: number
  message?: string
  error?: string
}

export interface TokenResponse {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
  scope?: string
}

export interface AppConfig {
  OAUTH_CLIENT_ID: string
  OAUTH_SCOPES: string
  OAUTH_REDIRECT_URI: string
  ENABLE_DEVTOOLS: boolean
  API_BASE_URL?: string
  ENVIRONMENT?: string
}

export interface OAuthState {
  state: string
  codeVerifier: string
  redirectUri?: string
}

// API response types
export interface ApiResponse<T = any> {
  data: T
  message?: string
  success: boolean
  timestamp: string
}

export interface ApiError {
  message: string
  code: string
  details?: Record<string, any>
  timestamp: string
}

// Pagination types
export interface PaginationMeta {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: PaginationMeta
}

// Project related types
export enum ProjectManagementType {
  PILOTAGE = 'PILOTAGE',
  FULL_MANAGEMENT = 'FULL_MANAGEMENT'
}

export interface Project extends BaseEntity {
  name: string
  description?: string
  status: ProjectStatus
  priority: ProjectPriority
  startDate: string
  endDate: string
  clientName: string  // Mandatory field
  projectManager: string
  budget: number
  category: string
  location: string
  tags: string
  multiPhase?: boolean
  managementType?: ProjectManagementType | string
  ownerId?: string
  collaborators?: string[]
  lastModified?: string | Date
  // UI consistency fields
  creationDate: string
  estimatedStartDate: string  // Mandatory field
  estimatedEndDate: string    // Mandatory field
}

export interface ProjectCreateRequest {
  name: string
  description?: string
  status?: ProjectStatus
  priority?: ProjectPriority
  startDate?: string
  endDate?: string
  clientName: string           // Mandatory field
  projectManager?: string
  budget?: number
  category?: string
  location?: string
  tags?: string
  collaborators?: string[]
  estimatedStartDate: string   // Mandatory field
  estimatedEndDate: string     // Mandatory field
  managementType?: ProjectManagementType | string
}

export interface ProjectUpdateRequest {
  name?: string
  description?: string
  status?: ProjectStatus
  priority?: ProjectPriority
  startDate?: string
  endDate?: string
  clientName?: string
  projectManager?: string
  budget?: number
  category?: string
  location?: string
  tags?: string
  collaborators?: string[]
}

export enum ProjectStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  PLANNED = 'planned',
  ON_HOLD = 'on_hold',
  COMPLETED = 'completed',
  ARCHIVED = 'archived'
}

export enum ProjectPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ProjectQueryParams {
  page?: number
  size?: number
  sort?: string
  name?: string
  status?: ProjectStatus
  priority?: ProjectPriority
  clientName?: string
  projectManager?: string
  category?: string
  tags?: string
  startDateFrom?: string
  startDateTo?: string
  endDateFrom?: string
  endDateTo?: string
  budgetMin?: number
  budgetMax?: number
  location?: string
}

export interface ProjectStats {
  active: number
  planned: number
  onHold: number
  completed: number
  total: number
  totalBudget: number
  avgBudget: number
}

export interface ProjectPhoto {
  id: string | number
  projectId: string | number
  originalName: string
  fileName: string
  url: string
  size: number
  type: string
  uploadedAt: string
  description?: string
  order?: number
}

export interface BudgetReport {
  projectId: string | number
  projectName: string
  totalBudget: number
  consumedBudget: number
  remainingBudget: number
  consumptionPercentage: number
  categories: BudgetCategory[]
}

export interface BudgetCategory {
  name: string
  allocated: number
  spent: number
  remaining: number
  percentage: number
}

export interface TaskBudgetReport {
  projectId: string | number
  projectName: string
  totalBudget: number
  tasks: TaskBudgetInfo[]
}

export interface TaskBudgetInfo {
  taskId: string | number
  taskName: string
  estimatedCost: number
  actualCost: number
  variance: number
  status: string
}

export interface IntervenantBudgetReport {
  projectId: string | number
  projectName: string
  totalBudget: number
  intervenants: IntervenantBudgetInfo[]
}

export interface IntervenantBudgetInfo {
  intervenantId: string | number
  name: string
  role: string
  totalCost: number
  hours: number
  hourlyRate: number
}

// File types
export interface FileUpload {
  file: File
  name: string
  type: string
  size: number
}

export interface UploadedFile extends BaseEntity {
  filename: string
  originalName: string
  mimeType: string
  size: number
  url: string
  projectId?: string
}

// UI component types
export interface SelectOption<T = any> {
  label: string
  value: T
  disabled?: boolean
  icon?: string
}

export interface TableColumn {
  key: string
  label: string
  sortable?: boolean
  width?: string
  align?: 'left' | 'center' | 'right'
  type?: 'text' | 'number' | 'date' | 'boolean' | 'action'
}

// Notification types
export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message: string
  duration?: number
  actions?: NotificationAction[]
}

export interface NotificationAction {
  label: string
  action: () => void
  style?: 'primary' | 'secondary' | 'danger'
}

// Form validation types
export interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  custom?: (value: any) => boolean | string
}

export interface FormField {
  name: string
  label: string
  type: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'file'
  value: any
  rules?: ValidationRule[]
  options?: SelectOption[]
  placeholder?: string
  disabled?: boolean
}

// Chart types (for Chart.js integration)
export interface ChartConfig {
  type: 'line' | 'bar' | 'pie' | 'doughnut' | 'radar' | 'scatter'
  data: {
    labels: string[]
    datasets: ChartDataset[]
  }
  options?: Record<string, any>
}

export interface ChartDataset {
  label: string
  data: number[]
  backgroundColor?: string | string[]
  borderColor?: string | string[]
  borderWidth?: number
}

// Router types
export interface RouteConfig {
  path: string
  name: string
  component: any
  meta?: {
    requiresAuth?: boolean
    title?: string
    icon?: string
  }
  children?: RouteConfig[]
}

// Store types (Vuex)
export interface RootState {
  selectedFirm: any | null // Will be typed properly when firm types are available
  user: User | null
  isAuthenticated: boolean
  accessToken: string | null
  refreshToken: string | null
  loading: boolean
  error: string | null
  success: string | null
  modal: any | null
  messages: Record<string, any>
  currentProject: Project | null
  // Whether initial auth check has completed after app boot
  authChecked?: boolean
}

export interface ProjectState {
  projects: Project[]
  currentProject: Project | null
  loading: boolean
  error: string | null
}

export interface NotificationState {
  notifications: Notification[]
}

export interface UIState {
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  language: string
  loading: boolean
}

// Utility types
export type Nullable<T> = T | null
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>
export type RequiredBy<T, K extends keyof T> = T & Required<Pick<T, K>>

// Vue component props helper types
export type ComponentProps<T> = T extends new (...args: any) => any
  ? InstanceType<T>['$props']
  : never

// Event handler types
export type EventHandler<T = Event> = (event: T) => void
export type AsyncEventHandler<T = Event> = (event: T) => Promise<void>

// Toast notification types (for useNotifications composable)
export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastAction {
  label?: string
  callback?: () => void
  type?: 'confirm'
  onConfirm?: () => void
  onCancel?: () => void
}

export interface ToastNotificationOptions {
  message: string
  type?: ToastType
  duration?: number
  closable?: boolean
  action?: ToastAction | null
  title?: string | null
}

export interface ToastNotification {
  id: number
  message: string
  type: ToastType
  duration: number
  closable: boolean
  action: ToastAction | null
  title: string | null
  createdAt: number
  isVisible: boolean
}

// AG Grid types
export interface GridColumnDef {
  headerName: string
  field: string
  sortable?: boolean
  filter?: boolean | string
  width?: number
  minWidth?: number
  maxWidth?: number
  resizable?: boolean
  cellRenderer?: string | any
  cellEditor?: string | any
  editable?: boolean
  pinned?: 'left' | 'right'
}

export interface GridOptions {
  columnDefs: GridColumnDef[]
  rowData: any[]
  pagination?: boolean
  paginationPageSize?: number
  enableSorting?: boolean
  enableFilter?: boolean
  rowSelection?: 'single' | 'multiple'
  onRowClicked?: (event: any) => void
  onRowDoubleClicked?: (event: any) => void
  onSelectionChanged?: (event: any) => void
}

// Additional pagination types
export interface PaginationParams {
  page?: number
  size?: number
}

// Validation result type
export interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

// Subscription plan types
export interface SubscriptionFeature {
  id: number
  name: string
  description: string
}

export interface SubscriptionPlan {
  id: number
  name: string
  description: string
  version: number
  effectiveDate: string
  status: string
  planType: 'FREE' | 'TRIAL' | 'PAID' | 'ENTERPRISE'
  maxProjects: number
  maxUsers: number
  maxStorageGb: number
  price: number | null
  priceTTC: number | null
  tvaRate: number | null
  tvaAmount: number | null
  currency: string
  features: SubscriptionFeature[]
  createdAt: string
  updatedAt: string
}

export interface CreateSubscriptionPlanRequest {
  name: string
  version: number
  effectiveDate: string
  description?: string
  maxProjects: number
  maxUsers: number
  maxStorageGb: number
  price: number | null
  currency: string
  status: string
  planType: 'FREE' | 'TRIAL' | 'PAID' | 'ENTERPRISE'
  features: string[]
}

export interface UpdateSubscriptionPlanRequest {
  name: string
  version: number
  effectiveDate: string
  description?: string
  maxProjects: number
  maxUsers: number
  maxStorageGb: number
  price: number | null
  currency: string
  status: string
  planType: 'FREE' | 'TRIAL' | 'PAID' | 'ENTERPRISE'
  features: string[]
}

// Admin firm subscription types
export interface FirmSubscription {
  firmId: number
  firmName: string
  phone?: string
  email?: string
  planId?: number | null
  planName?: string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  // Usage stats
  projectsCount?: number
  usersCount?: number
  usedStorageBytes?: number
  // Billing and discount information
  billingPeriod?: 'MONTHLY' | 'ANNUAL' | null
  discountPercentage?: number | null
  basePrice?: number | null
  basePriceHT?: number | null
  totalAmountHT?: number | null
  tvaRate?: number | null
  tvaAmount?: number | null
  totalAmount?: number | null
}

export interface AssignPlanRequest {
  planId: number
}

export interface UpdateEndDateRequest {
  endDate: string
}

export interface FirmUsageDto {
  firmId: number
  firmName: string
  projectsCount: number
  usersCount: number
  usedStorageBytes: number
  usedStorageFormatted: string
}

// Tender module types
export * from './tender'

// User Notification types
export interface UserNotificationDTO {
  id: number
  userId: number
  type: string
  read: boolean
  createdAt: string
  projectId?: number
  referenceId?: number
  contextData?: Record<string, any>
}
